/**
 * Serialise, parse, validate and migrate a `DesignDocument`.
 *
 * The JSON pane, the persistence service and the "paste & apply" flow all go
 * through here, so there is exactly one definition of what a valid document is.
 * `parseDesignDocument` is deliberately forgiving — it repairs what it can
 * (missing defaults, bad numbers) and only rejects what it cannot interpret,
 * because the text it is handed may have been typed by a user.
 */

import {
  DEFAULT_AUTO_LAYOUT,
  DEFAULT_CANVAS,
  DEFAULT_ICON,
  DEFAULT_TEXT,
  WIDGET_PALETTE_META,
} from '../design-builder.constants';
import { defaultAppearance } from '../renderers/design-appearance.spec';
import { CONFIG_ONLY_LAYOUT_TYPES } from '../renderers/design-sample-data';
import type {
  DesignDocument,
  DesignLayer,
  DesignNode,
  DesignNodeType,
  DesignPage,
} from '../design-builder.types';
import { LAYER_KIND_LABELS, isLayerKind } from './design-layer.utils';
import {
  createDocumentId,
  createNodeId,
  createPageId,
  createScreenFrame,
  withUniqueComponentIds,
} from './design-node.utils';
import { compactBinding, isLiveOnlyWidget, withoutSavedDropdownOptions, withoutStoredRows } from './design-publish.utils';
import { normalizeIconAction } from './design-icon-actions.utils';
import { autoRefreshSecondsOf } from './design-refresh.utils';
import { normalizeCanvasFilters } from './design-canvas-filter.utils';
import { inputBoxBindingKeys } from './design-input-data.utils';

const NODE_TYPES: readonly DesignNodeType[] = [
  'FRAME', 'GROUP', 'RECT', 'ELLIPSE', 'LINE', 'TEXT', 'IMAGE', 'ICON', 'WIDGET',
];

// ── Creation ─────────────────────────────────────────────────────────────────

/**
 * A new design starts with exactly ONE screen already on the canvas.
 *
 * A design with no screen has nothing to publish, and a screen is not something
 * you would ever want two of here — so it is created for you rather than being
 * a palette item you must remember to drag out first.
 */
export function createEmptyDocument(name = 'Untitled design'): DesignDocument {
  const now = new Date().toISOString();
  return {
    schemaVersion: 1,
    id: createDocumentId(),
    name,
    slug: slugify(name),
    canvas: { ...DEFAULT_CANVAS, grid: { ...DEFAULT_CANVAS.grid } },
    pages: [
      {
        id: createPageId(),
        name: 'Page 1',
        nodes: [createScreenFrame(DEFAULT_CANVAS.width)],
      },
    ],
    meta: { createdAt: now, updatedAt: now, status: 'draft' },
  };
}

export function slugify(value: string): string {
  return (value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80) || 'untitled-design';
}

// ── Serialise ────────────────────────────────────────────────────────────────

export function serializeDesignDocument(document: DesignDocument, pretty = true): string {
  return JSON.stringify(toStoredDocument(document), null, pretty ? 2 : 0);
}

/** Just one subtree — what the JSON pane shows when scoped to the selection. */
export function serializeNode(node: DesignNode, pretty = true): string {
  return JSON.stringify(toStoredNode(node), null, pretty ? 2 : 0);
}

// ── Stored shape ─────────────────────────────────────────────────────────────

/**
 * The document as it is WRITTEN — to the JSON pane, the template store, the
 * draft and the published row.
 *
 * A screen's nodes are nested inside the layer they belong to, and `layerId`
 * disappears: the nesting already says it, so nothing is stored twice. Each
 * layer is then a self-contained `{ id, name, kind, nodes }` object — the exact
 * thing that becomes one JSON column per kind (docs/DESIGN_BUILDER.md §4 Layers).
 *
 * In memory the editor keeps the flat `children` array instead, because every
 * node utility, the canvas and all three renderers walk `children` and the
 * array order IS the paint order. This pair of functions is the only place the
 * two shapes meet.
 */
export function toStoredDocument(document: DesignDocument): Record<string, unknown> {
  return {
    ...document,
    pages: document.pages.map((page) => ({ ...page, nodes: page.nodes.map(toStoredNode) })),
  };
}

export function toStoredNode(node: DesignNode): Record<string, unknown> {
  const { children = [], layers, layerId: _dropped, ...rest } = node;
  const stored: Record<string, unknown> = { ...rest };
  // A nested table loads its rows from the compute API every time: none are saved.
  if (isLiveOnlyWidget(rest.widget)) stored['widget'] = { ...rest.widget, data: withoutStoredRows(rest.widget!.data) };
  // Nor a dropdown's options: they come from the compute API on open.
  if (rest.widget?.layoutType === 'DROPDOWN') stored['widget'] = withoutSavedDropdownOptions(rest.widget);

  if (layers?.length) {
    const grouped = new Map(layers.map((layer) => [layer.id, [] as Record<string, unknown>[]]));
    const loose: Record<string, unknown>[] = [];
    for (const child of children) {
      (grouped.get(child.layerId ?? '') ?? loose).push(toStoredNode(child));
    }
    stored['layers'] = layers.map((layer) => ({ ...layer, nodes: grouped.get(layer.id) ?? [] }));
    // A node in no band at all cannot happen once the store has synced, but it
    // would be the one thing a write could silently drop.
    if (loose.length) stored['children'] = loose;
    return stored;
  }

  if (children.length) stored['children'] = children.map(toStoredNode);
  return stored;
}

// ── Parse & validate ─────────────────────────────────────────────────────────

export interface ParseResult {
  document: DesignDocument | null;
  error: string | null;
}

export function parseDesignDocument(text: string): ParseResult {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch (error) {
    return { document: null, error: (error as Error).message };
  }
  if (!isRecord(raw)) return { document: null, error: 'Expected a JSON object at the top level.' };

  const pages = Array.isArray(raw['pages']) ? raw['pages'] : null;
  if (!pages) return { document: null, error: 'Missing "pages" — expected an array of pages.' };

  const normalized = normalizeDocument(raw);
  if (!normalized.pages.length) {
    return { document: null, error: '"pages" must contain at least one page.' };
  }
  return { document: normalized, error: null };
}

/** Coerce an arbitrary object into a complete, renderable document. */
export function normalizeDocument(raw: Record<string, unknown>): DesignDocument {
  const now = new Date().toISOString();
  const canvas = isRecord(raw['canvas']) ? raw['canvas'] : {};
  const grid = isRecord(canvas['grid']) ? canvas['grid'] : {};
  const meta = isRecord(raw['meta']) ? raw['meta'] : {};
  const name = str(raw['name'], 'Untitled design');
  const canvasFilters = normalizeCanvasFilters(canvas['filters']);

  return {
    schemaVersion: 1,
    id: str(raw['id'], createDocumentId()),
    name,
    slug: str(raw['slug'], slugify(name)),
    ...(raw['projectId'] ? { projectId: str(raw['projectId'], '') } : {}),
    ...(raw['projectName'] ? { projectName: str(raw['projectName'], '') } : {}),
    canvas: {
      width: num(canvas['width'], DEFAULT_CANVAS.width),
      height: num(canvas['height'], DEFAULT_CANVAS.height),
      background: str(canvas['background'], DEFAULT_CANVAS.background),
      grid: {
        size: num(grid['size'], DEFAULT_CANVAS.grid.size),
        snap: bool(grid['snap'], DEFAULT_CANVAS.grid.snap),
        visible: bool(grid['visible'], DEFAULT_CANVAS.grid.visible),
      },
      ...(autoRefreshSecondsOf(canvas['autoRefreshSeconds'])
        ? { autoRefreshSeconds: autoRefreshSecondsOf(canvas['autoRefreshSeconds']) }
        : {}),
      ...(canvasFilters.length ? { filters: canvasFilters } : {}),
    },
    // Copies made before duplicate gave them their own componentId share one: split them.
    pages: withUniqueComponentIds((Array.isArray(raw['pages']) ? raw['pages'] : [])
      .filter(isRecord)
      .map(normalizePage)),
    ...(isRecord(raw['theme']) ? { theme: raw['theme'] as DesignDocument['theme'] } : {}),
    meta: {
      createdAt: str(meta['createdAt'], now),
      updatedAt: str(meta['updatedAt'], now),
      status: meta['status'] === 'published' ? 'published' : 'draft',
      ...(meta['author'] ? { author: str(meta['author'], '') } : {}),
      ...(meta['publishedAt'] ? { publishedAt: str(meta['publishedAt'], '') } : {}),
    },
  };
}

function normalizePage(raw: Record<string, unknown>): DesignPage {
  return {
    id: str(raw['id'], createPageId()),
    name: str(raw['name'], 'Page 1'),
    nodes: (Array.isArray(raw['nodes']) ? raw['nodes'] : []).filter(isRecord).map(normalizeNode),
  };
}

function normalizeNode(raw: Record<string, unknown>): DesignNode {
  const type = NODE_TYPES.includes(raw['type'] as DesignNodeType)
    ? (raw['type'] as DesignNodeType)
    : 'RECT';

  const node: DesignNode = {
    id: str(raw['id'], createNodeId()),
    name: str(raw['name'], type.toLowerCase()),
    type,
    x: num(raw['x'], 0),
    y: num(raw['y'], 0),
    width: Math.max(1, num(raw['width'], 100)),
    height: Math.max(1, num(raw['height'], 100)),
    ...(typeof raw['groupScaleX'] === 'number'
      ? { groupScaleX: Math.max(0.01, num(raw['groupScaleX'], 1)) }
      : {}),
    ...(typeof raw['groupScaleY'] === 'number'
      ? { groupScaleY: Math.max(0.01, num(raw['groupScaleY'], 1)) }
      : {}),
    rotation: num(raw['rotation'], 0),
    visible: bool(raw['visible'], true),
    locked: bool(raw['locked'], false),
    opacity: clamp01(num(raw['opacity'], 1)),
    style: isRecord(raw['style']) ? (raw['style'] as DesignNode['style']) : {},
  };

  if (Array.isArray(raw['children'])) {
    node.children = raw['children'].filter(isRecord).map(normalizeNode);
  } else if (type === 'FRAME' || type === 'GROUP') {
    node.children = [];
  }

  if (type === 'GROUP' && Array.isArray(raw['memberIds'])) {
    node.memberIds = raw['memberIds'].filter((id): id is string => typeof id === 'string');
  }

  // `layerId` is only written by older saves; the stored shape nests instead.
  if (typeof raw['layerId'] === 'string' && raw['layerId']) node.layerId = raw['layerId'];

  if (type === 'FRAME' && Array.isArray(raw['layers'])) {
    const layers: DesignLayer[] = [];
    const nested: DesignNode[] = [];

    for (const entry of raw['layers'].filter(isRecord)) {
      if (typeof entry['id'] !== 'string' || !isLayerKind(entry['kind'])) continue;
      const kind = entry['kind'] as DesignLayer['kind'];
      const layer: DesignLayer = {
        id: entry['id'],
        kind,
        name: str(entry['name'], LAYER_KIND_LABELS[kind]),
      };
      layers.push(layer);
      for (const child of (Array.isArray(entry['nodes']) ? entry['nodes'] : []).filter(isRecord)) {
        const parsed = normalizeNode(child);
        parsed.layerId = layer.id;
        nested.push(parsed);
      }
    }

    if (layers.length) node.layers = layers;
    // Layer order is paint order, so the bands come first and anything stored
    // loose (older rows) keeps its place in front of them.
    if (nested.length) node.children = [...nested, ...(node.children ?? [])];
  }

  // Both are merged onto defaults rather than trusted wholesale, so a partial
  // object from hand-edited JSON still yields a renderable node.
  if (isRecord(raw['layout'])) {
    node.layout = { ...DEFAULT_AUTO_LAYOUT, ...raw['layout'] } as NonNullable<DesignNode['layout']>;
  }
  if (raw['fluid'] === true) node.fluid = true;
  if (raw['popupScreen'] === true) node.popupScreen = true;
  if (isRecord(raw['screenSize'])) {
    const size = raw['screenSize'];
    const screenSize = {
      ...(typeof size['width'] === 'number' && size['width'] > 0 ? { width: size['width'] } : {}),
      ...(typeof size['height'] === 'number' && size['height'] > 0 ? { height: size['height'] } : {}),
    };
    if (Object.keys(screenSize).length) node.screenSize = screenSize;
  }
  if (isRecord(raw['slide']) && Array.isArray(raw['slide']['stopIds'])) {
    const stopIds = raw['slide']['stopIds'].filter((id): id is string => typeof id === 'string');
    const followerIds = Array.isArray(raw['slide']['followerIds'])
      ? raw['slide']['followerIds'].filter((id): id is string => typeof id === 'string')
      : [];
    const overlap = raw['slide']['avoidOverlap'];
    // 'push' (stops stepped aside) was replaced by 'nearest' (stops stay).
    const avoidOverlap = overlap === 'push' ? 'nearest' : overlap === 'nearest' || overlap === 'beside' || overlap === 'above' || overlap === 'below' || overlap === 'none' ? overlap : undefined;
    if (stopIds.length) node.slide = { stopIds, ...(followerIds.length ? { followerIds } : {}), ...(avoidOverlap ? { avoidOverlap } : {}) };
  }
  if (isRecord(raw['levelTexts']) && isRecord(raw['levelTexts']['texts'])) {
    const texts = Object.fromEntries(Object.entries(raw['levelTexts']['texts']).filter((entry): entry is [string, string] => typeof entry[1] === 'string'));
    const sliderId = typeof raw['levelTexts']['sliderId'] === 'string' ? raw['levelTexts']['sliderId'] : '';
    if (Object.keys(texts).length || sliderId) node.levelTexts = { texts, ...(sliderId ? { sliderId } : {}) };
  }
  if (isRecord(raw['showAtLevels']) && Array.isArray(raw['showAtLevels']['levels'])) {
    const levels = raw['showAtLevels']['levels'].filter((level): level is string => typeof level === 'string' && !!level);
    const sliderId = typeof raw['showAtLevels']['sliderId'] === 'string' ? raw['showAtLevels']['sliderId'] : '';
    if (levels.length) node.showAtLevels = { levels, ...(sliderId ? { sliderId } : {}) };
  }
  if (isRecord(raw['image'])) {
    const image = raw['image'];
    node.image = {
      src: str(image['src'], ''),
      fit: (['cover', 'contain', 'fill', 'none'] as const).includes(image['fit'] as never)
        ? (image['fit'] as NonNullable<DesignNode['image']>['fit'])
        : 'cover',
      alt: str(image['alt'], ''),
    };
  }

  if (type === 'ICON') {
    const icon = isRecord(raw['icon']) ? raw['icon'] : {};
    node.icon = {
      name: str(icon['name'], DEFAULT_ICON.name),
      color: str(icon['color'], DEFAULT_ICON.color),
      strokeWidth: num(icon['strokeWidth'], DEFAULT_ICON.strokeWidth),
      absoluteStrokeWidth: bool(icon['absoluteStrokeWidth'], false),
      fill: str(icon['fill'], 'none'),
      action: normalizeIconAction(icon['action']),
      targetNodeId: str(icon['targetNodeId'], ''),
      ...(isRecord(icon['info']) ? { info: {
        source: icon['info']['source'] === 'api' ? 'api' as const : 'static' as const,
        title: str(icon['info']['title'], 'Information'),
        text: str(icon['info']['text'], ''),
        url: str(icon['info']['url'], ''),
        responsePath: str(icon['info']['responsePath'], ''),
      } } : {}),
    };
  }

  if (type === 'TEXT') {
    const text = isRecord(raw['text']) ? raw['text'] : {};
    node.text = { ...DEFAULT_TEXT, ...text } as DesignNode['text'];
    const binding = isRecord(raw['dataBinding']) ? raw['dataBinding'] : null;
    if (binding && str(binding['dataset'], '') && str(binding['field'], '')) {
      node.dataBinding = {
        dataset: str(binding['dataset'], ''),
        field: str(binding['field'], ''),
        aggregation: str(binding['aggregation'], 'SUM'),
        // Without this the text loses its component on every round trip, and
        // its computed value has nothing to key off — so it renders blank.
        ...(binding['componentId'] ? { componentId: str(binding['componentId'], '') } : {}),
        ...(typeof binding['scale'] === 'string' ? { scale: binding['scale'] as NonNullable<DesignNode['dataBinding']>['scale'] } : {}),
        ...(typeof binding['decimals'] === 'number' ? { decimals: binding['decimals'] } : {}),
        ...(typeof binding['prefix'] === 'string' ? { prefix: binding['prefix'] } : {}),
        ...(typeof binding['suffix'] === 'string' ? { suffix: binding['suffix'] } : {}),
        ...(typeof binding['statusValue'] === 'string' ? { statusValue: binding['statusValue'] } : {}),
        ...(typeof binding['countField'] === 'string' ? { countField: binding['countField'] } : {}),
        // "Only rows where": dropped here, a saved / published text lost its filter.
        ...(typeof binding['filterField'] === 'string' && binding['filterField'] ? { filterField: binding['filterField'] } : {}),
        ...(typeof binding['filterOperator'] === 'string' && binding['filterOperator'] ? { filterOperator: binding['filterOperator'] } : {}),
        ...(Array.isArray(binding['filterValues']) ? { filterValues: (binding['filterValues'] as unknown[]).map(String) } : {}),
      };
    }
    const formula = isRecord(raw['formula']) ? raw['formula'] : null;
    if (formula && str(formula['expression'], '').trim()) {
      node.formula = {
        expression: str(formula['expression'], ''),
        ...(typeof formula['scale'] === 'string' ? { scale: formula['scale'] as NonNullable<DesignNode['formula']>['scale'] } : {}),
        ...(typeof formula['decimals'] === 'number' ? { decimals: formula['decimals'] } : {}),
        ...(typeof formula['prefix'] === 'string' ? { prefix: formula['prefix'] } : {}),
        ...(typeof formula['suffix'] === 'string' ? { suffix: formula['suffix'] } : {}),
      };
    }
    if (Array.isArray(raw['valueColors'])) {
      const ranges = raw['valueColors'].filter(isRecord).map((range) => ({
        ...(typeof range['from'] === 'number' && Number.isFinite(range['from']) ? { from: range['from'] } : {}),
        ...(typeof range['to'] === 'number' && Number.isFinite(range['to']) ? { to: range['to'] } : {}),
        ...(typeof range['fromId'] === 'string' && range['fromId'] ? { fromId: range['fromId'] } : {}),
        ...(typeof range['toId'] === 'string' && range['toId'] ? { toId: range['toId'] } : {}),
        color: str(range['color'], ''),
      })).filter((range) => !!range.color);
      if (ranges.length) node.valueColors = ranges;
    }
    if (raw['valueColorTarget'] === 'pill') node.valueColorTarget = 'pill';
    if (typeof raw['valueColorSourceId'] === 'string' && raw['valueColorSourceId']) node.valueColorSourceId = raw['valueColorSourceId'];
    if (typeof raw['initialsOfId'] === 'string' && raw['initialsOfId']) node.initialsOfId = raw['initialsOfId'];
  }

  if (type === 'WIDGET') {
    const widget = isRecord(raw['widget']) ? raw['widget'] : {};
    const layoutType = str(widget['layoutType'], 'KPI');
    const build = isRecord(widget['build']) ? (widget['build'] as Record<string, unknown>) : null;
    // A binding that round-tripped through an older publish holds a full copy
    // of `build.payload`; keep only the summary so the mapping has one home.
    const binding = compactBinding(
      isRecord(build?.['payload']) ? (build!['payload'] as Record<string, unknown>) : undefined,
      isRecord(widget['binding']) ? (widget['binding'] as Record<string, unknown>) : undefined,
    );
    // A zone slider's binding IS its mapping (value / min / max / cost columns,
    // zone source, levels): keeping only the generic keys above dropped it on
    // every reload and publish, so the slider fell back to its static numbers.
    if (layoutType === 'ZONE_SLIDER' && isRecord(widget['binding'])) {
      Object.assign(binding, widget['binding'] as Record<string, unknown>);
    }
    // An input box's "Fill from data" link and Save settings (see design-input-data.utils).
    if (layoutType === 'INPUT_BOX' && isRecord(widget['binding'])) {
      Object.assign(binding, inputBoxBindingKeys(widget['binding'] as Record<string, unknown>));
    }
    const data =isRecord(widget['data']) ? (widget['data'] as Record<string, unknown>) : null;

    node.widget = {
      layoutType,
      data,
      // `appearance` is the DESIGN TIER — variant, orientation, every format
      // control. Leaving it out here silently discarded the whole design pass
      // on reload: the draft round-trips through this function, so a refresh
      // reset every chart to its defaults.
      appearance: isRecord(widget['appearance'])
        ? (widget['appearance'] as Record<string, unknown>)
        : defaultAppearance(layoutType),
      binding,
      format: isRecord(widget['format']) ? (widget['format'] as Record<string, unknown>) : {},
      ...(widget['filterKey'] ? { filterKey: str(widget['filterKey'], '') } : {}),
      listenerNodeIds: Array.isArray(widget['listenerNodeIds'])
        ? widget['listenerNodeIds'].filter((id): id is string => typeof id === 'string')
        : [],
      ...(widget['componentId'] ? { componentId: str(widget['componentId'], '') } : {}),
      // Losing this flag stopped the inspector regenerating sample data, so
      // changing the variant updated the label but not the chart. Until the
      // data tier exists, any data present IS sample data, so an unbound
      // widget that has data is treated as sampled.
      sample: bool(widget['sample'], !!data && !binding['dataset'] && !CONFIG_ONLY_LAYOUT_TYPES.has(layoutType)),
      ...(build ? { build } : {}),
    };

    migrateInputControlNode(node);
  }

  return node;
}

/**
 * One-time repair for input-control widgets saved before they had their own
 * defaults: they were given the chart CARD style (12px padding, clipped) and a
 * height that could not fit a label plus the control, so they rendered half
 * cut. Only grows a node that is too short and only stops it clipping — the
 * position and any deliberate resizing are left alone.
 */
function migrateInputControlNode(node: DesignNode): void {
  const layoutType = node.widget?.layoutType ?? '';
  if (!INPUT_CONTROL_LAYOUT_TYPES.has(layoutType)) return;

  const minHeight = WIDGET_PALETTE_META[layoutType]?.height ?? 104;

  const legacyCardStyle = node.style?.overflow === 'hidden' || !!node.style?.padding?.some((edge) => edge > 0);
  // A control intentionally made smaller by resizing a GROUP carries visual
  // scale metadata. Growing it back here would break the group after reload.
  if (legacyCardStyle && node.groupScaleY === undefined && node.height < minHeight) node.height = minHeight;

  if (node.style?.overflow === 'hidden') node.style.overflow = 'visible';
  if (node.style?.padding?.some((edge) => edge > 0)) node.style.padding = [0, 0, 0, 0];
}

/** Widget families that render a form control rather than a chart card. */
const INPUT_CONTROL_LAYOUT_TYPES: ReadonlySet<string> = new Set([
  'DROPDOWN',
  'SLIDER',
  'INPUT_BOX',
  'FILE_UPLOAD',
]);

// ── Validation report ────────────────────────────────────────────────────────

export interface ValidationIssue {
  path: string;
  message: string;
}

/**
 * Non-blocking checks surfaced in the JSON pane — a document can render with
 * these present, but they usually mean something was wired wrong.
 */
export function validateDocument(document: DesignDocument): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const seen = new Set<string>();

  const walk = (nodes: DesignNode[], path: string): void => {
    nodes.forEach((node, index) => {
      const here = `${path}[${index}]`;
      if (seen.has(node.id)) {
        issues.push({ path: here, message: `Duplicate node id "${node.id}".` });
      }
      seen.add(node.id);

      if (node.type === 'WIDGET' && !node.widget?.layoutType) {
        issues.push({ path: here, message: 'Widget node has no layoutType.' });
      }
      if (node.type === 'IMAGE' && !node.image?.src) {
        issues.push({ path: here, message: 'Image node has no source.' });
      }
      if (node.children?.length) walk(node.children, `${here}.children`);
    });
  };

  document.pages.forEach((page, index) => walk(page.nodes, `pages[${index}].nodes`));

  // Cross-filter listeners must point at nodes that still exist.
  const ids = new Set(seen);
  document.pages.forEach((page, pageIndex) => {
    const check = (nodes: DesignNode[], path: string): void => {
      nodes.forEach((node, index) => {
        const here = `${path}[${index}]`;
        for (const listener of node.widget?.listenerNodeIds ?? []) {
          if (!ids.has(listener)) {
            issues.push({ path: here, message: `Listener "${listener}" does not exist.` });
          }
        }
        if (node.children?.length) check(node.children, `${here}.children`);
      });
    };
    check(page.nodes, `pages[${pageIndex}].nodes`);
  });

  return issues;
}

// ── Coercion helpers ─────────────────────────────────────────────────────────

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function str(value: unknown, fallback: string): string {
  return typeof value === 'string' && value ? value : fallback;
}

function num(value: unknown, fallback: number): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function bool(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}
